/**
 * MonthPicker — calendario de grilla mensual, controlado, para elegir UNA fecha.
 *
 * Reemplaza al `<input type="date">` nativo en los flujos de agendamiento del
 * portal (RescheduleForm, AppointmentScheduler) para que se sientan como la
 * agenda y no como un formulario (H14 / P1-4 de docs/UX_VISION.md).
 *
 * NO hace fetch de disponibilidad — eso es del consumidor. Tampoco orquesta
 * scroll: el consumidor reacciona a `onChange` como quiera.
 *
 * `value` / `minDate` son strings 'YYYY-MM-DD' en hora local de Santiago
 * (mismo formato que usa `toSantiagoISO`). Todo el cálculo de grilla es local,
 * sin `Date` en UTC, para no correrse un día en UTC-3/-4.
 */
import React, { useMemo, useState } from 'react';
import './ui.css';

const MONTH_NAMES = [
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre',
];
const WEEKDAYS = ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'];

/** 'YYYY-MM-DD' local, sin conversión UTC. */
const toDateStr = (y: number, m: number, d: number): string =>
  `${y}-${String(m + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;

export const todayDateStr = (): string => {
  const d = new Date();
  return toDateStr(d.getFullYear(), d.getMonth(), d.getDate());
};

interface MonthCell {
  dateStr: string;
  day: number;
  isCurrentMonth: boolean;
  isToday: boolean;
}

/** Grilla fija de 6 semanas (42 celdas), lunes primero. */
const buildMonthGrid = (year: number, month: number): MonthCell[] => {
  const today = todayDateStr();
  const firstOfMonth = new Date(year, month, 1);
  const leadingBlanks = (firstOfMonth.getDay() + 6) % 7; // Lun=0 … Dom=6
  const start = new Date(year, month, 1 - leadingBlanks);

  const cells: MonthCell[] = [];
  for (let i = 0; i < 42; i++) {
    const d = new Date(start.getFullYear(), start.getMonth(), start.getDate() + i);
    const dateStr = toDateStr(d.getFullYear(), d.getMonth(), d.getDate());
    cells.push({
      dateStr,
      day: d.getDate(),
      isCurrentMonth: d.getMonth() === month,
      isToday: dateStr === today,
    });
  }
  return cells;
};

interface MonthPickerProps {
  /** Fecha seleccionada, 'YYYY-MM-DD'. '' = ninguna. */
  value: string;
  onChange: (dateStr: string) => void;
  /** Fecha mínima seleccionable, 'YYYY-MM-DD'. Default: hoy. */
  minDate?: string;
  /** Etiqueta accesible del calendario. */
  ariaLabel?: string;
}

const MonthPicker: React.FC<MonthPickerProps> = ({
  value,
  onChange,
  minDate,
  ariaLabel = 'Elegí una fecha',
}) => {
  const min = minDate ?? todayDateStr();

  // Mes visible — arranca en el mes de `value`, salvo que sea anterior a `min`.
  const [viewDate, setViewDate] = useState(() => {
    const base = value && value >= min ? value : min;
    const [y, m] = base.split('-').map(Number);
    return { year: y, month: m - 1 };
  });

  const monthGrid = useMemo(
    () => buildMonthGrid(viewDate.year, viewDate.month),
    [viewDate.year, viewDate.month],
  );

  const [minY, minM] = min.split('-').map(Number);
  const prevDisabled =
    viewDate.year < minY || (viewDate.year === minY && viewDate.month <= minM - 1);

  const shiftMonth = (delta: number) => {
    setViewDate(prev => {
      const d = new Date(prev.year, prev.month + delta, 1);
      return { year: d.getFullYear(), month: d.getMonth() };
    });
  };

  return (
    <div className="ccui-monthpicker" role="group" aria-label={ariaLabel}>
      <div className="ccui-monthpicker__header">
        <button
          type="button"
          className="ccui-monthpicker__nav"
          onClick={() => shiftMonth(-1)}
          disabled={prevDisabled}
          aria-label="Mes anterior"
        >
          <i className="fa-solid fa-chevron-left" aria-hidden="true" />
        </button>
        <span className="ccui-monthpicker__title" aria-live="polite">
          {MONTH_NAMES[viewDate.month]} {viewDate.year}
        </span>
        <button
          type="button"
          className="ccui-monthpicker__nav"
          onClick={() => shiftMonth(1)}
          aria-label="Mes siguiente"
        >
          <i className="fa-solid fa-chevron-right" aria-hidden="true" />
        </button>
      </div>

      <div className="ccui-monthpicker__grid">
        {WEEKDAYS.map(d => (
          <span key={d} className="ccui-monthpicker__weekday">{d}</span>
        ))}
        {monthGrid.map(cell => {
          const selectable = cell.isCurrentMonth && cell.dateStr >= min;
          const isSelected = selectable && cell.dateStr === value;
          return (
            <button
              key={cell.dateStr}
              type="button"
              className={
                'ccui-monthpicker__day' +
                (isSelected ? ' is-selected' : '') +
                (cell.isToday ? ' is-today' : '') +
                (!cell.isCurrentMonth ? ' is-outside' : '')
              }
              disabled={!selectable}
              aria-pressed={isSelected}
              aria-current={cell.isToday ? 'date' : undefined}
              onClick={() => onChange(cell.dateStr)}
            >
              {cell.day}
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default MonthPicker;
